/**
 * Music theory for Bandmate (SPEC §4.3). Every export is a pure function of
 * its arguments: keys and scales, pitch names, scale-degree naming, roman
 * numeral / chord-name parsing, voice leading, chord rhythms, bass patterns,
 * root inference, Krumhansl–Schmuckler key detection, template-based chord
 * suggestion, the compact note format used by `get_track`, and the note-token
 * resolver used by `write_notes`.
 *
 * Conventions
 * - Pitches are MIDI numbers (60 = C4); pitch classes are 0..11 with C = 0.
 * - Steps are absolute (0..63, 16 per bar) as in `types.ts`; the compact
 *   note format and `write_notes` speak 1-based bars/steps at the boundary.
 * - Keys are spelled with sharps; flats are accepted on input everywhere.
 * - Roman numerals: case = triad quality; the degree is a position in the
 *   MODE's own scale. A `b` prefix lowers the PARALLEL-MAJOR degree
 *   (A minor `bVI` → F, reported as `VI`); a `#` prefix raises the mode's
 *   own degree (`#iv°` → D#dim, `#VII` in minor → G#). In a minor key a
 *   lowercase `vii` sits on the raised (harmonic) seventh: `vii°` → G#dim.
 *   Harmonic-minor chords are reported as `altered`, never `borrowed`.
 * - Symbol input is lenient: inner whitespace is dropped and multi-letter
 *   suffixes are case-insensitive (`F maj7`, `FMaj7`, `iiDIM`); the one-letter
 *   `M`/`m` distinction is kept. `voice` and `chordRhythmNotes` also accept
 *   `ChordSlot`-shaped `{ pitches, bar? }` entries so the chord strip can
 *   re-voice one bar around the others.
 */
import type { BassPattern, ChordRhythm, Key, MelodicTrackId, Mode, Note, StyleId } from '../types';
import {
  BARS,
  BASS_ROOT_RANGE,
  STEPS_PER_BAR,
  TOTAL_STEPS,
  TRACK_HOME_OCTAVE,
  TRACK_RANGE,
  VOICING_CENTER,
  VOICING_RANGE,
  splitStep,
} from '../types';

// ---------------------------------------------------------------------------
// Scales, pitch classes, names
// ---------------------------------------------------------------------------

/** Semitone intervals of the major scale from the tonic. */
export const MAJOR_INTERVALS: readonly number[] = [0, 2, 4, 5, 7, 9, 11];
/** Semitone intervals of the natural minor scale from the tonic. */
export const MINOR_INTERVALS: readonly number[] = [0, 2, 3, 5, 7, 8, 10];
/** Semitone intervals of the harmonic minor scale (natural minor with a raised 7th). */
export const HARMONIC_MINOR_INTERVALS: readonly number[] = [0, 2, 3, 5, 7, 8, 11];
/** Pitch-class names spelled with sharps, C = 0. */
export const PC_NAMES: readonly string[] = ['C', 'C#', 'D', 'D#', 'E', 'F', 'F#', 'G', 'G#', 'A', 'A#', 'B'];

const LETTER_PC: Readonly<Record<string, number>> = { C: 0, D: 2, E: 4, F: 5, G: 7, A: 9, B: 11 };
const ROMAN: readonly string[] = ['I', 'II', 'III', 'IV', 'V', 'VI', 'VII'];
const DEFAULT_VELOCITY = 0.8;

/** Wrap any integer into 0..11. */
export function mod12(n: number): number {
  return ((n % 12) + 12) % 12;
}

/** Pitch class of a key's tonic. */
export function tonicPc(key: Key): number {
  return LETTER_PC[key.tonic[0]!]! + (key.tonic.length > 1 ? 1 : 0);
}

/** Scale intervals for a mode. */
export function modeIntervals(mode: Mode): readonly number[] {
  return mode === 'major' ? MAJOR_INTERVALS : MINOR_INTERVALS;
}

/** Human-readable key name, e.g. "A minor". */
export function keyName(key: Key): string {
  return `${key.tonic} ${key.mode}`;
}

/** Name of a pitch class with sharps, e.g. 10 → "A#". */
export function pcName(pc: number): string {
  return PC_NAMES[mod12(pc)]!;
}

/** The seven pitch classes of the key's scale, tonic first (natural minor for `minor`). */
export function scalePitchClasses(key: Key): number[] {
  const t = tonicPc(key);
  return modeIntervals(key.mode).map((i) => mod12(t + i));
}

/** MIDI pitch → scientific name with sharps, e.g. 60 → "C4", 61 → "C#4". */
export function pitchToName(pitch: number): string {
  const p = Math.round(pitch);
  return `${PC_NAMES[mod12(p)]}${Math.floor(p / 12) - 1}`;
}

/**
 * Scientific pitch name → MIDI pitch. Accepts sharps and flats (`Bb3`, `F#2`,
 * `B♭3`), case-insensitive letters, and negative octaves (`C-1` = 0).
 * Returns null for anything malformed or outside 0..127.
 */
export function nameToPitch(name: string): number | null {
  const m = /^([A-Ga-g])([#b♯♭]?)(-?\d{1,2})$/.exec(name.trim());
  if (!m) return null;
  const base = LETTER_PC[m[1]!.toUpperCase()]!;
  const acc = m[2] === '#' || m[2] === '♯' ? 1 : m[2] === 'b' || m[2] === '♭' ? -1 : 0;
  const octave = Number(m[3]);
  const pitch = (octave + 1) * 12 + base + acc;
  return pitch >= 0 && pitch <= 127 ? pitch : null;
}

// ---------------------------------------------------------------------------
// Scale degrees
// ---------------------------------------------------------------------------

/** A pitch described relative to a key: `label` is `"3"`, `"b3"`, `"#4"`, … */
export interface DegreeInfo {
  degree: 1 | 2 | 3 | 4 | 5 | 6 | 7;
  accidental: '' | '#' | 'b';
  label: string;
}

/**
 * Name a pitch as a degree of the key (SPEC §4.3): 1–7 are the mode's own
 * scale; chromatic notes are `#4`/`#7` when they are the raised 4th/7th and
 * otherwise `b` of the degree above (C major: b2 b3 #4 b6 b7; A minor:
 * b2 b4 #4 b7 #7).
 */
export function degreeOf(pitch: number, key: Key): DegreeInfo {
  const rel = mod12(pitch - tonicPc(key));
  const scale = modeIntervals(key.mode);
  const idx = scale.indexOf(rel);
  if (idx >= 0) {
    const degree = (idx + 1) as DegreeInfo['degree'];
    return { degree, accidental: '', label: String(degree) };
  }
  if (rel === scale[3]! + 1) return { degree: 4, accidental: '#', label: '#4' };
  if (rel === scale[6]! + 1) return { degree: 7, accidental: '#', label: '#7' };
  const above = scale.findIndex((s) => s > rel);
  const degree = (above + 1) as DegreeInfo['degree'];
  return { degree, accidental: 'b', label: `b${degree}` };
}

// ---------------------------------------------------------------------------
// Chord qualities
// ---------------------------------------------------------------------------

/** Chord quality vocabulary shared by numerals and chord names. */
export type ChordQuality =
  | 'maj'
  | 'min'
  | 'dim'
  | 'aug'
  | 'sus2'
  | 'sus4'
  | 'dom7'
  | 'maj7'
  | 'min7'
  | 'minmaj7'
  | 'dim7'
  | 'half_dim7'
  | 'maj6'
  | 'min6'
  | 'dom9'
  | 'min9';

/** Semitones from the root for each quality. */
export const QUALITY_INTERVALS: Readonly<Record<ChordQuality, readonly number[]>> = {
  maj: [0, 4, 7],
  min: [0, 3, 7],
  dim: [0, 3, 6],
  aug: [0, 4, 8],
  sus2: [0, 2, 7],
  sus4: [0, 5, 7],
  dom7: [0, 4, 7, 10],
  maj7: [0, 4, 7, 11],
  min7: [0, 3, 7, 10],
  minmaj7: [0, 3, 7, 11],
  dim7: [0, 3, 6, 9],
  half_dim7: [0, 3, 6, 10],
  maj6: [0, 4, 7, 9],
  min6: [0, 3, 7, 9],
  dom9: [0, 4, 7, 10, 14],
  min9: [0, 3, 7, 10, 14],
};

/** Qualities written with a lowercase numeral. */
const LOWERCASE_QUALITIES: ReadonlySet<ChordQuality> = new Set<ChordQuality>([
  'min',
  'min7',
  'minmaj7',
  'min6',
  'min9',
  'dim',
  'dim7',
  'half_dim7',
]);
const DIM_FAMILY: ReadonlySet<ChordQuality> = new Set<ChordQuality>(['dim', 'dim7', 'half_dim7']);

/** Canonical numeral suffix per quality (SPEC spellings: `°`, `ø7`, `+`). */
const NUMERAL_SUFFIX: Readonly<Record<ChordQuality, string>> = {
  maj: '',
  min: '',
  dim: '°',
  aug: '+',
  sus2: 'sus2',
  sus4: 'sus4',
  dom7: '7',
  maj7: 'maj7',
  min7: '7',
  minmaj7: 'maj7',
  dim7: '°7',
  half_dim7: 'ø7',
  maj6: '6',
  min6: '6',
  dom9: '9',
  min9: '9',
};

/** Canonical chord-name suffix per quality. */
const NAME_SUFFIX: Readonly<Record<ChordQuality, string>> = {
  maj: '',
  min: 'm',
  dim: 'dim',
  aug: 'aug',
  sus2: 'sus2',
  sus4: 'sus4',
  dom7: '7',
  maj7: 'maj7',
  min7: 'm7',
  minmaj7: 'mMaj7',
  dim7: 'dim7',
  half_dim7: 'm7b5',
  maj6: '6',
  min6: 'm6',
  dom9: '9',
  min9: 'm9',
};

/** Numeral suffixes → quality. Case-dependent entries take a function. */
const NUMERAL_SUFFIXES: Readonly<Record<string, ChordQuality | ((lower: boolean) => ChordQuality)>> = {
  '': (lower) => (lower ? 'min' : 'maj'),
  '7': (lower) => (lower ? 'min7' : 'dom7'),
  maj7: (lower) => (lower ? 'minmaj7' : 'maj7'),
  M7: (lower) => (lower ? 'minmaj7' : 'maj7'),
  m: 'min',
  m7: 'min7',
  sus2: 'sus2',
  sus4: 'sus4',
  dim: 'dim',
  '°': 'dim',
  o: 'dim',
  dim7: 'dim7',
  '°7': 'dim7',
  o7: 'dim7',
  '+': 'aug',
  aug: 'aug',
  'ø': 'half_dim7',
  'ø7': 'half_dim7',
  m7b5: 'half_dim7',
  sus: 'sus4',
  '6': (lower) => (lower ? 'min6' : 'maj6'),
  m6: 'min6',
  '9': (lower) => (lower ? 'min9' : 'dom9'),
  m9: 'min9',
};

/** Chord-name suffixes → quality. */
const NAME_SUFFIXES: Readonly<Record<string, ChordQuality>> = {
  '': 'maj',
  maj: 'maj',
  M: 'maj',
  m: 'min',
  min: 'min',
  '-': 'min',
  '7': 'dom7',
  maj7: 'maj7',
  M7: 'maj7',
  'Δ7': 'maj7',
  'Δ': 'maj7',
  m7: 'min7',
  min7: 'min7',
  '-7': 'min7',
  mMaj7: 'minmaj7',
  mM7: 'minmaj7',
  minmaj7: 'minmaj7',
  'm(maj7)': 'minmaj7',
  dim: 'dim',
  '°': 'dim',
  o: 'dim',
  dim7: 'dim7',
  '°7': 'dim7',
  o7: 'dim7',
  m7b5: 'half_dim7',
  min7b5: 'half_dim7',
  'ø': 'half_dim7',
  'ø7': 'half_dim7',
  aug: 'aug',
  '+': 'aug',
  sus2: 'sus2',
  sus4: 'sus4',
  sus: 'sus4',
  '6': 'maj6',
  maj6: 'maj6',
  m6: 'min6',
  min6: 'min6',
  '9': 'dom9',
  m9: 'min9',
  min9: 'min9',
};

/** The suffix vocabulary quoted in parse errors (SPEC §4.3). */
export const CHORD_SUFFIXES: readonly string[] = [
  '7',
  'maj7',
  'm7',
  'sus2',
  'sus4',
  'dim',
  '°',
  '+',
  'ø',
  'ø7',
  'm7b5',
  '°7',
  '6',
  '9',
];

/** Example forms listed in every `ChordParseError.accepted`. */
export const ACCEPTED_CHORD_FORMS: readonly string[] = [
  'I',
  'ii',
  'iii',
  'IV',
  'V',
  'vi',
  'vii°',
  'i',
  'ii°',
  'III',
  'iv',
  'v',
  'VI',
  'VII',
  'bVI',
  'bVII',
  'bII',
  '#iv°',
  'V7',
  'i7',
  'iiø7',
  'Imaj7',
  'VImaj7',
  'Isus4',
  'C',
  'Am',
  'Fmaj7',
  'G7',
  'Bb',
  'Bm7b5',
  'C#dim',
  'Csus2',
  'C6',
  'C9',
  'Caug',
];

// ---------------------------------------------------------------------------
// Chord symbol parsing
// ---------------------------------------------------------------------------

/** A chord resolved against a key, whether it was written as a numeral or a name. */
export interface ParsedChord {
  /** The symbol as given. */
  input: string;
  /** Normalised roman numeral, e.g. "VI", "V7", "iiø7", "bVII". */
  numeral: string;
  /** Chord name spelled with sharps, e.g. "F", "E7", "Bm7b5". */
  name: string;
  /** Root pitch class 0..11. */
  rootPc: number;
  quality: ChordQuality;
  /** Semitones from the root, low to high. */
  intervals: number[];
  /** Outside the key's diatonic (and, in minor, harmonic) set. */
  borrowed: boolean;
  /** Harmonic-minor chord in a minor key (V, V7, vii°, vii°7, …). */
  altered: boolean;
  /** Set when an accidental prefix was folded away (A minor `bVI` → `VI`). */
  normalizedFrom?: string;
  /** Advice such as "ii° is diatonic here". */
  hint?: string;
}

/** Returned by `parseChordSymbol` for unparseable input. */
export interface ChordParseError {
  error: string;
  /** Accepted forms, for the agent to retry with. */
  accepted: string[];
}

/** Type guard for `parseChordSymbol` results. */
export function isChordParseError(r: ParsedChord | ChordParseError): r is ChordParseError {
  return 'error' in r;
}

function parseError(message: string): ChordParseError {
  return { error: message, accepted: [...ACCEPTED_CHORD_FORMS] };
}

/**
 * Look a suffix up in a table. Exact spelling wins; otherwise suffixes of two or
 * more characters are retried lower-cased (`Maj7`, `MIN7`, `DIM`), so the
 * one-letter `M` (major) / `m` (minor) distinction is never folded away.
 */
function lookupSuffix<T>(table: Readonly<Record<string, T>>, suffix: string): T | undefined {
  const exact = table[suffix];
  if (exact !== undefined) return exact;
  if (suffix.length < 2) return undefined;
  const lower = suffix.toLowerCase();
  return lower === suffix ? undefined : table[lower];
}

function normaliseSymbolText(s: string): string {
  return s
    .trim()
    .replace(/\s+/g, '')
    .replace(/♭/g, 'b')
    .replace(/♯/g, '#')
    .replace(/º/g, '°')
    .replace(/Ø/g, 'ø');
}

function scaleSet(key: Key): ReadonlySet<number> {
  return new Set(scalePitchClasses(key));
}

function harmonicMinorSet(key: Key): ReadonlySet<number> {
  const t = tonicPc(key);
  return new Set(HARMONIC_MINOR_INTERVALS.map((i) => mod12(t + i)));
}

function chordPcs(rootPc: number, quality: ChordQuality): number[] {
  return QUALITY_INTERVALS[quality].map((i) => mod12(rootPc + i));
}

/** Diatonic / altered classification of a chord in a key. */
function classify(rootPc: number, quality: ChordQuality, key: Key): { diatonic: boolean; altered: boolean } {
  const pcs = chordPcs(rootPc, quality);
  const scale = scaleSet(key);
  if (pcs.every((pc) => scale.has(pc))) return { diatonic: true, altered: false };
  if (key.mode === 'minor') {
    const harmonic = harmonicMinorSet(key);
    if (pcs.every((pc) => harmonic.has(pc))) return { diatonic: false, altered: true };
  }
  return { diatonic: false, altered: false };
}

/** Prefix + degree index (0..6) of the canonical numeral for a root in a key. */
function numeralCore(rootPc: number, quality: ChordQuality, key: Key): { prefix: '' | 'b' | '#'; degree: number } {
  const rel = mod12(rootPc - tonicPc(key));
  const scale = modeIntervals(key.mode);
  const lower = LOWERCASE_QUALITIES.has(quality);
  const idx = scale.indexOf(rel);
  if (key.mode === 'minor') {
    if (rel === 11) return { prefix: lower ? '' : '#', degree: 6 };
    if (rel === 10 && lower) return { prefix: 'b', degree: 6 };
  }
  if (idx >= 0) return { prefix: '', degree: idx };
  const below = scale.indexOf(mod12(rel - 1));
  if (DIM_FAMILY.has(quality) && below >= 0) return { prefix: '#', degree: below };
  const table: Readonly<Record<number, ['b' | '#', number]>> =
    key.mode === 'major'
      ? { 1: ['b', 1], 3: ['b', 2], 6: ['#', 3], 8: ['b', 5], 10: ['b', 6] }
      : { 1: ['b', 1], 4: ['b', 3], 6: ['#', 3], 9: ['#', 5] };
  const [prefix, degree] = table[rel]!;
  return { prefix, degree };
}

/** Canonical roman numeral for a root/quality in a key. */
export function numeralFor(rootPc: number, quality: ChordQuality, key: Key): string {
  const { prefix, degree } = numeralCore(rootPc, quality, key);
  const roman = LOWERCASE_QUALITIES.has(quality) ? ROMAN[degree]!.toLowerCase() : ROMAN[degree]!;
  return `${prefix}${roman}${NUMERAL_SUFFIX[quality]}`;
}

/** Canonical chord name (sharps) for a root/quality. */
export function chordName(rootPc: number, quality: ChordQuality): string {
  return `${pcName(rootPc)}${NAME_SUFFIX[quality]}`;
}

/** Build the full result once root and quality are known. */
function finalizeChord(input: string, rootPc: number, quality: ChordQuality, key: Key, normalizedFrom?: string): ParsedChord {
  const { diatonic, altered } = classify(rootPc, quality, key);
  const borrowed = !diatonic && !altered;
  const chord: ParsedChord = {
    input,
    numeral: numeralFor(rootPc, quality, key),
    name: chordName(rootPc, quality),
    rootPc: mod12(rootPc),
    quality,
    intervals: [...QUALITY_INTERVALS[quality]],
    borrowed,
    altered,
  };
  if (normalizedFrom !== undefined) chord.normalizedFrom = normalizedFrom;
  if (borrowed && (quality === 'maj' || quality === 'min' || quality === 'dim')) {
    for (const alt of ['min', 'maj', 'dim'] as const) {
      if (alt === quality) continue;
      const c = classify(rootPc, alt, key);
      if (c.diatonic || c.altered) {
        chord.hint = `${numeralFor(rootPc, alt, key)} is diatonic here`;
        break;
      }
    }
  }
  return chord;
}

function parseNumeral(input: string, text: string, key: Key): ParsedChord | ChordParseError | null {
  const m = /^([b#]?)([IiVv]+)(.*)$/.exec(text);
  if (!m) return null;
  const acc = m[1] as '' | 'b' | '#';
  const romanText = m[2]!;
  const suffix = m[3]!;
  const isUpper = romanText === romanText.toUpperCase();
  const isLower = romanText === romanText.toLowerCase();
  if (!isUpper && !isLower) return parseError(`mixed-case numeral '${romanText}' in '${input}': use all upper (major) or all lower (minor)`);
  const degree = ROMAN.indexOf(romanText.toUpperCase());
  if (degree < 0) return parseError(`unknown numeral '${romanText}' in '${input}': use I..VII (major) or i..vii (minor)`);
  const rule = lookupSuffix(NUMERAL_SUFFIXES, suffix);
  if (rule === undefined) {
    return parseError(`unknown chord suffix '${suffix}' in '${input}'; accepted suffixes: ${CHORD_SUFFIXES.join(' ')}`);
  }
  const quality = typeof rule === 'function' ? rule(isLower) : rule;
  const tonic = tonicPc(key);
  const scale = modeIntervals(key.mode);
  let rootPc: number;
  if (acc === 'b') rootPc = mod12(tonic + MAJOR_INTERVALS[degree]! - 1);
  else if (acc === '#') rootPc = mod12(tonic + scale[degree]! + 1);
  else if (key.mode === 'minor' && degree === 6 && isLower) rootPc = mod12(tonic + 11);
  else rootPc = mod12(tonic + scale[degree]!);
  const canonical = numeralCore(rootPc, quality, key);
  const normalizedFrom = canonical.prefix !== acc || canonical.degree !== degree ? input.trim() : undefined;
  return finalizeChord(input, rootPc, quality, key, normalizedFrom);
}

function parseName(input: string, text: string, key: Key): ParsedChord | ChordParseError | null {
  const m = /^([A-G])([#b]?)(.*)$/.exec(text);
  if (!m) return null;
  const rootPc = mod12(LETTER_PC[m[1]!]! + (m[2] === '#' ? 1 : m[2] === 'b' ? -1 : 0));
  const quality = lookupSuffix(NAME_SUFFIXES, m[3]!);
  if (quality === undefined) {
    return parseError(`unknown chord suffix '${m[3]}' in '${input}'; accepted suffixes: ${CHORD_SUFFIXES.join(' ')}`);
  }
  return finalizeChord(input, rootPc, quality, key);
}

/**
 * Parse a roman numeral (`VI`, `bVII`, `iiø7`, `#iv°`, `V7`) or a chord name
 * (`Am`, `Fmaj7`, `G7`, `Bb`, `Bm7b5`, `C#dim`) against a key. The result
 * echoes both forms; errors list accepted forms.
 */
export function parseChordSymbol(symbol: string, key: Key): ParsedChord | ChordParseError {
  const text = normaliseSymbolText(symbol);
  if (!text) return parseError('empty chord symbol: give a roman numeral like VI or a chord name like Fmaj7');
  const asName = parseName(symbol, text, key);
  if (asName) return asName;
  const asNumeral = parseNumeral(symbol, text, key);
  if (asNumeral) return asNumeral;
  return parseError(`cannot read '${symbol.trim()}' as a roman numeral (I..VII / i..vii, optional b/# prefix) or a chord name (A..G with # or b)`);
}

// ---------------------------------------------------------------------------
// Voicing
// ---------------------------------------------------------------------------

/** Every closed-position inversion of a chord, at every octave that fits `[lo, hi]`. */
function voicingCandidates(chord: ParsedChord, lo: number, hi: number): number[][] {
  const n = chord.intervals.length;
  const out: number[][] = [];
  for (let inv = 0; inv < n; inv++) {
    const rotated = [...chord.intervals.slice(inv), ...chord.intervals.slice(0, inv)];
    const stack: number[] = [];
    for (const iv of rotated) {
      let p = mod12(chord.rootPc + iv);
      while (stack.length && p <= stack[stack.length - 1]!) p += 12;
      stack.push(p);
    }
    const min = stack[0]!;
    const max = stack[stack.length - 1]!;
    const kMin = Math.ceil((lo - min) / 12);
    const kMax = Math.floor((hi - max) / 12);
    for (let k = kMin; k <= kMax; k++) out.push(stack.map((p) => p + 12 * k));
  }
  if (out.length === 0) {
    // The range is narrower than the chord: fall back to root position nearest the centre.
    const stack = chord.intervals.map((iv) => chord.rootPc + iv);
    const centre = (lo + hi) / 2;
    const k = Math.round((centre - (stack[0]! + stack[stack.length - 1]!) / 2) / 12);
    out.push(stack.map((p) => p + 12 * k));
  }
  return out;
}

function centreDistance(v: number[], centre: number): number {
  return v.reduce((s, p) => s + Math.abs(p - centre), 0);
}

/** Total voice movement between two voicings; sizes may differ. */
function movement(a: number[], b: number[]): number {
  if (a.length === b.length) return a.reduce((s, p, i) => s + Math.abs(p - b[i]!), 0);
  const [big, small] = a.length > b.length ? [a, b] : [b, a];
  return big.reduce((s, p) => s + Math.min(...small.map((q) => Math.abs(p - q))), 0);
}

/** A voicing that is already decided (e.g. an existing `ChordSlot`): kept verbatim by `voice`. */
export interface FixedVoicing {
  /** MIDI pitches; any order, sorted on output. */
  pitches: readonly number[];
}

/** What `voice` accepts per bar: a parsed chord to search, or a fixed voicing to keep. */
export type VoiceInput = ParsedChord | FixedVoicing;

function isParsedChordLike(x: VoiceInput): x is ParsedChord {
  const c = x as Partial<ParsedChord>;
  return Array.isArray(c.intervals) && c.intervals.length > 0 && typeof c.rootPc === 'number';
}

/**
 * Voice a chord sequence (SPEC §4.3): enumerate inversions × octave shifts
 * inside `range` (default `VOICING_RANGE`), minimise total movement from the
 * previous voicing (ties → distance to `center`); the first chord is the
 * candidate closest to the centre. Returns one ascending pitch list per chord.
 *
 * An entry may instead be a `FixedVoicing` (`{ pitches }`, as a `ChordSlot`
 * is): it is kept as given (sorted) and its neighbours are voiced around it,
 * which is what the chord strip needs when one bar is re-typed. An entry with
 * neither a parsable chord nor pitches yields an empty voicing.
 */
export function voice(chords: VoiceInput[], opts: { center?: number; range?: readonly [number, number] } = {}): number[][] {
  const centre = opts.center ?? VOICING_CENTER;
  const [lo, hi] = opts.range ?? VOICING_RANGE;
  const out: number[][] = [];
  let prev: number[] | null = null;
  for (const chord of chords) {
    let candidates: number[][];
    if (isParsedChordLike(chord)) candidates = voicingCandidates(chord, lo, hi);
    else {
      const fixed = ((chord as Partial<FixedVoicing>).pitches ?? []).filter((p) => Number.isFinite(p)).slice().sort((a, b) => a - b);
      candidates = fixed.length ? [fixed] : [];
    }
    let best: number[] | null = null;
    let bestPrimary = Infinity;
    let bestSecondary = Infinity;
    for (const cand of candidates) {
      const secondary = centreDistance(cand, centre);
      const primary = prev ? movement(prev, cand) : secondary;
      if (primary < bestPrimary || (primary === bestPrimary && secondary < bestSecondary)) {
        best = cand;
        bestPrimary = primary;
        bestSecondary = secondary;
      }
    }
    if (best) prev = best;
    out.push(best ?? []);
  }
  return out;
}

// ---------------------------------------------------------------------------
// Chord rhythms
// ---------------------------------------------------------------------------

/** A voicing with an optional home bar (0..3), e.g. a `ChordSlot`. */
export interface BarVoicing {
  pitches: readonly number[];
  /** 0..3; when every entry carries one, entries are placed by bar instead of positionally. */
  bar?: number;
}

/** What `chordRhythmNotes` accepts per entry: a plain pitch list or a `BarVoicing`. */
export type RhythmVoicing = readonly number[] | BarVoicing;

/**
 * Resolve rhythm input to one pitch list per bar (0..3). Plain lists (or
 * bar-less objects) are repeated positionally to fill 4 bars; when every entry
 * carries a `bar`, each is placed there and the other bars stay empty.
 */
function voicingsPerBar(voicings: readonly RhythmVoicing[]): number[][] {
  const pitchesOf = (v: RhythmVoicing): number[] =>
    (Array.isArray(v) ? (v as readonly number[]) : ((v as BarVoicing).pitches ?? [])).filter((p) => Number.isFinite(p));
  const hasBar = (v: RhythmVoicing): v is BarVoicing => !Array.isArray(v) && Number.isInteger((v as BarVoicing).bar);
  const perBar: number[][] = Array.from({ length: BARS }, () => []);
  if (voicings.length === 0) return perBar;
  if (voicings.every(hasBar)) {
    for (const v of voicings) if (v.bar! >= 0 && v.bar! < BARS) perBar[v.bar!] = pitchesOf(v);
    return perBar;
  }
  return perBar.map((_, bar) => pitchesOf(voicings[bar % voicings.length]!));
}

/**
 * Render voicings (one per bar; fewer are repeated to fill 4 bars) with a
 * chord rhythm (SPEC §4.3): `pad` step1 L16 · `stab` step1 L2 + step7 L2 ·
 * `arp` 8ths up (1,3,5,7) then down (9,11,13,15) L1 · `pulse` 8ths L1 ·
 * `offbeat` steps 3,7,11,15 L1. Output is sorted by step then pitch.
 *
 * Entries may be plain pitch lists (`number[][]`, repeated positionally) or
 * `ChordSlot`-like objects; when every entry has a `bar` (0..3) it is placed
 * in that bar and bars without an entry stay silent.
 */
export function chordRhythmNotes(voicings: readonly RhythmVoicing[], rhythm: ChordRhythm): Omit<Note, 'by'>[] {
  const perBar = voicingsPerBar(voicings);
  const out: Omit<Note, 'by'>[] = [];
  const add = (step: number, pitch: number, length: number): void => {
    out.push({ step, pitch, length, velocity: DEFAULT_VELOCITY });
  };
  for (let bar = 0; bar < BARS; bar++) {
    const v = perBar[bar]!;
    if (v.length === 0) continue;
    const base = bar * STEPS_PER_BAR;
    switch (rhythm) {
      case 'pad':
        for (const p of v) add(base, p, 16);
        break;
      case 'stab':
        for (const p of v) {
          add(base, p, 2);
          add(base + 6, p, 2);
        }
        break;
      case 'arp': {
        const n = v.length;
        const walk: number[] = [];
        for (let i = 0; i < 4; i++) {
          const p = v[i % n]! + 12 * Math.floor(i / n);
          walk.push(p <= TRACK_RANGE.chords[1] ? p : v[n - 1]!);
        }
        const seq = [...walk, ...walk.slice().reverse()];
        seq.forEach((p, i) => add(base + i * 2, p, 1));
        break;
      }
      case 'pulse':
        for (let s = 0; s < STEPS_PER_BAR; s += 2) for (const p of v) add(base + s, p, 1);
        break;
      case 'offbeat':
        for (const s of [2, 6, 10, 14]) for (const p of v) add(base + s, p, 1);
        break;
    }
  }
  return out.sort((a, b) => a.step - b.step || a.pitch - b.pitch);
}

// ---------------------------------------------------------------------------
// Bass
// ---------------------------------------------------------------------------

/** Per-bar chord skeleton used by `bassNotes`. */
export interface BarRoot {
  /** 0..3 */
  bar: number;
  rootPc: number;
  thirdPc: number;
  fifthPc: number;
}

/** Options for `bassNotes`. */
export interface BassOptions {
  /** `-12` for `octave: 1`; the result never drops below the bass window. */
  octaveShift?: 0 | -12;
  /** Absolute kick steps (0..63) for `follow_kick`. */
  kickSteps?: number[];
  /** `root` plays one L4 note for lofi and 8ths for house/synthwave. Default lofi. */
  style?: StyleId;
}

/** Wrap a pitch class into `BASS_ROOT_RANGE` (D2–D3), then shift and keep inside the bass window. */
function bassRootPitch(pc: number, shift: number): number {
  let p = BASS_ROOT_RANGE[0] + mod12(pc - BASS_ROOT_RANGE[0]) + shift;
  while (p < TRACK_RANGE.bass[0]) p += 12;
  while (p > TRACK_RANGE.bass[1]) p -= 12;
  return p;
}

/**
 * Generate bass notes from per-bar roots (SPEC §4.3). `root` = root@1 L4
 * (lofi) or 8ths (house/synthwave); `root_fifth` = root@1 root@5 fifth@9
 * root@13 (fifth below when the root is above 46); `octave` = root/root+12
 * alternating 8ths; `walking` = root@1 3rd@5 5th@9 approach@13 (next root −1;
 * last bar → 5th); `follow_kick` = root on every kick step, held until the
 * next kick (max 4), falling back to `root` when no kicks are given. Roots
 * are wrapped into `BASS_ROOT_RANGE`; `octaveShift: -12` drops an octave.
 */
export function bassNotes(roots: BarRoot[], pattern: BassPattern, opts: BassOptions = {}): Omit<Note, 'by'>[] {
  const shift = opts.octaveShift ?? 0;
  const sorted = [...roots].filter((r) => r.bar >= 0 && r.bar < BARS).sort((a, b) => a.bar - b.bar);
  const out: Omit<Note, 'by'>[] = [];
  const add = (step: number, pitch: number, length: number): void => {
    out.push({ step, pitch, length, velocity: DEFAULT_VELOCITY });
  };
  const kicks = [...new Set((opts.kickSteps ?? []).filter((s) => Number.isInteger(s) && s >= 0 && s < TOTAL_STEPS))].sort(
    (a, b) => a - b,
  );
  const effective: BassPattern = pattern === 'follow_kick' && kicks.length === 0 ? 'root' : pattern;
  const eighths = opts.style === 'house' || opts.style === 'synthwave';

  sorted.forEach((r, i) => {
    const base = r.bar * STEPS_PER_BAR;
    const root = bassRootPitch(r.rootPc, shift);
    const third = root + mod12(r.thirdPc - r.rootPc);
    const fifthInterval = mod12(r.fifthPc - r.rootPc);
    const fifthAbove = root + fifthInterval;
    switch (effective) {
      case 'root':
        if (eighths) for (let s = 0; s < STEPS_PER_BAR; s += 2) add(base + s, root, 1);
        else add(base, root, 4);
        break;
      case 'root_fifth': {
        const fifth = root > 46 ? fifthAbove - 12 : fifthAbove;
        add(base, root, 4);
        add(base + 4, root, 4);
        add(base + 8, fifth, 4);
        add(base + 12, root, 4);
        break;
      }
      case 'octave':
        for (let s = 0; s < STEPS_PER_BAR; s += 2) add(base + s, s % 4 === 0 ? root : root + 12, 1);
        break;
      case 'walking': {
        const next = sorted[i + 1];
        let approach = next ? bassRootPitch(next.rootPc, shift) - 1 : fifthAbove;
        // A semitone below the next root can fall under the bass window (E1 → D#1); approach from above instead.
        if (approach < TRACK_RANGE.bass[0]) approach += 2;
        add(base, root, 4);
        add(base + 4, third, 4);
        add(base + 8, fifthAbove, 4);
        add(base + 12, approach, 4);
        break;
      }
      case 'follow_kick':
        for (let k = 0; k < kicks.length; k++) {
          const step = kicks[k]!;
          if (step < base || step >= base + STEPS_PER_BAR) continue;
          const nextKick = k + 1 < kicks.length ? kicks[k + 1]! : kicks[0]! + TOTAL_STEPS;
          const length = Math.max(1, Math.min(4, nextKick - step, TOTAL_STEPS - step));
          add(step, root, length);
        }
        break;
    }
  });
  return out.sort((a, b) => a.step - b.step || a.pitch - b.pitch);
}

// ---------------------------------------------------------------------------
// Root inference from the chords track
// ---------------------------------------------------------------------------

/**
 * Infer a per-bar root/third/fifth from chords-track notes (used by `add_bass`
 * when no progression metadata exists). For each bar, the notes sounding on
 * the bar's first step are used (else the earliest notes in the bar); the root
 * is the pitch class that best stacks in thirds over the others, ties going to
 * the lowest note. The third/fifth are read from the notes present, defaulting
 * to a minor third and perfect fifth. Bars without notes carry the neighbouring
 * bar. Returns null when the track is empty.
 */
export function inferRootsFromChordNotes(notes: Note[]): BarRoot[] | null {
  if (notes.length === 0) return null;
  const perBar: (BarRoot | null)[] = [];
  for (let bar = 0; bar < BARS; bar++) {
    const s0 = bar * STEPS_PER_BAR;
    let group = notes.filter((n) => n.step <= s0 && n.step + n.length > s0);
    if (group.length === 0) {
      const inBar = notes.filter((n) => n.step >= s0 && n.step < s0 + STEPS_PER_BAR);
      if (inBar.length) {
        const first = Math.min(...inBar.map((n) => n.step));
        group = inBar.filter((n) => n.step === first);
      }
    }
    perBar.push(group.length ? rootOfGroup(bar, group) : null);
  }
  if (perBar.every((r) => r === null)) return null;
  const out: BarRoot[] = [];
  for (let bar = 0; bar < BARS; bar++) {
    let r = perBar[bar];
    if (!r) {
      for (let b = bar - 1; b >= 0 && !r; b--) r = perBar[b];
      for (let b = bar + 1; b < BARS && !r; b++) r = perBar[b];
    }
    out.push({ ...r!, bar });
  }
  return out;
}

function rootOfGroup(bar: number, group: Note[]): BarRoot {
  const lowestByPc = new Map<number, number>();
  for (const n of group) {
    const pc = mod12(n.pitch);
    lowestByPc.set(pc, Math.min(n.pitch, lowestByPc.get(pc) ?? Infinity));
  }
  const has = (pc: number): boolean => lowestByPc.has(mod12(pc));
  let rootPc = -1;
  let bestScore = -Infinity;
  let bestPitch = Infinity;
  for (const [pc, lowest] of lowestByPc) {
    const score =
      (has(pc + 7) ? 2 : has(pc + 6) || has(pc + 8) ? 1 : 0) +
      (has(pc + 3) || has(pc + 4) ? 1 : 0) +
      (has(pc + 10) || has(pc + 11) ? 0.5 : 0);
    if (score > bestScore || (score === bestScore && lowest < bestPitch)) {
      rootPc = pc;
      bestScore = score;
      bestPitch = lowest;
    }
  }
  const thirdPc = has(rootPc + 4) ? mod12(rootPc + 4) : mod12(rootPc + 3);
  const fifthPc = has(rootPc + 7)
    ? mod12(rootPc + 7)
    : has(rootPc + 6)
      ? mod12(rootPc + 6)
      : has(rootPc + 8)
        ? mod12(rootPc + 8)
        : mod12(rootPc + 7);
  return { bar, rootPc, thirdPc, fifthPc };
}

// ---------------------------------------------------------------------------
// Key detection
// ---------------------------------------------------------------------------

/** Krumhansl–Kessler major key profile (tonic first). */
export const KS_MAJOR_PROFILE: readonly number[] = [6.35, 2.23, 3.48, 2.33, 4.38, 4.09, 2.52, 5.19, 2.39, 3.66, 2.29, 2.88];
/** Krumhansl–Kessler minor key profile (tonic first). */
export const KS_MINOR_PROFILE: readonly number[] = [6.33, 2.68, 3.52, 5.38, 2.6, 3.53, 2.54, 4.75, 3.98, 2.69, 3.34, 3.17];

const ALL_TONICS: readonly Key['tonic'][] = ['C', 'C#', 'D', 'D#', 'E', 'F', 'F#', 'G', 'G#', 'A', 'A#', 'B'];

/** True when the two keys are relative major/minor of each other (C major ↔ A minor). */
export function isRelativePair(a: Key, b: Key): boolean {
  if (a.mode === b.mode) return false;
  const major = a.mode === 'major' ? a : b;
  const minor = a.mode === 'major' ? b : a;
  return mod12(tonicPc(major) + 9) === tonicPc(minor);
}

function pearson(x: readonly number[], y: readonly number[]): number {
  const n = x.length;
  const mx = x.reduce((s, v) => s + v, 0) / n;
  const my = y.reduce((s, v) => s + v, 0) / n;
  let sxy = 0;
  let sxx = 0;
  let syy = 0;
  for (let i = 0; i < n; i++) {
    const dx = x[i]! - mx;
    const dy = y[i]! - my;
    sxy += dx * dy;
    sxx += dx * dx;
    syy += dy * dy;
  }
  if (sxx === 0 || syy === 0) return 0;
  return sxy / Math.sqrt(sxx * syy);
}

function round3(n: number): number {
  return Math.round(n * 1000) / 1000;
}

/**
 * Krumhansl–Schmuckler key finding (SPEC §4.3): pitch-class weights are note
 * lengths, with the bar-1 downbeat and the last note weighted ×2; each of the
 * 24 keys is scored by Pearson correlation with its profile. Returns the top
 * three with `confidence` = max(0, r). When the top two are a relative pair
 * within 0.05 and `sessionKey` is one of them, the session key is preferred.
 */
export function detectKey(notes: Note[], opts: { sessionKey?: Key } = {}): { key: Key; confidence: number }[] {
  if (notes.length === 0) return opts.sessionKey ? [{ key: opts.sessionKey, confidence: 0 }] : [];
  const weights = new Array<number>(12).fill(0);
  const lastStep = Math.max(...notes.map((n) => n.step));
  for (const n of notes) {
    let mult = 1;
    if (n.step === 0) mult *= 2;
    if (n.step === lastStep) mult *= 2;
    weights[mod12(n.pitch)] += Math.max(1, n.length) * mult;
  }
  const ranked: { key: Key; r: number }[] = [];
  for (const mode of ['major', 'minor'] as const) {
    const profile = mode === 'major' ? KS_MAJOR_PROFILE : KS_MINOR_PROFILE;
    for (let t = 0; t < 12; t++) {
      const rotated = weights.map((_, pc) => weights[mod12(pc + t)]!);
      ranked.push({ key: { tonic: ALL_TONICS[t]!, mode }, r: pearson(rotated, profile) });
    }
  }
  ranked.sort((a, b) => b.r - a.r);
  const [first, second] = ranked;
  if (
    opts.sessionKey &&
    first &&
    second &&
    isRelativePair(first.key, second.key) &&
    Math.abs(first.r - second.r) < 0.05 &&
    second.key.tonic === opts.sessionKey.tonic &&
    second.key.mode === opts.sessionKey.mode
  ) {
    ranked[0] = second;
    ranked[1] = first;
  }
  return ranked.slice(0, 3).map(({ key, r }) => ({ key, confidence: round3(Math.max(0, r)) }));
}

// ---------------------------------------------------------------------------
// Chord suggestion
// ---------------------------------------------------------------------------

/** Progression templates per mode (SPEC §4.3); the first three carry a 0.5 prior. */
export const CHORD_TEMPLATES: Readonly<Record<Mode, readonly (readonly string[])[]>> = {
  minor: [
    ['i', 'VI', 'III', 'VII'],
    ['i', 'iv', 'v', 'i'],
    ['i', 'VII', 'VI', 'V'],
    ['i', 'iv', 'VI', 'V'],
    ['i', 'VI', 'iv', 'V'],
    ['i', 'III', 'VII', 'iv'],
    ['VI', 'VII', 'i', 'i'],
    ['iiø7', 'V7', 'i', 'i'],
  ],
  major: [
    ['I', 'V', 'vi', 'IV'],
    ['I', 'vi', 'IV', 'V'],
    ['vi', 'IV', 'I', 'V'],
    ['I', 'IV', 'V', 'IV'],
    ['ii', 'V', 'I', 'I'],
    ['I', 'iii', 'IV', 'V'],
    ['IV', 'V', 'iii', 'vi'],
    ['I', 'IV', 'vi', 'V'],
  ],
};

/** One chord-progression suggestion. */
export interface ChordSuggestion {
  /** Numerals, one per bar (7ths for lofi). */
  progression: string[];
  /** Chord names, one per bar. */
  names: string[];
  /** 0..1 melody fit. */
  score: number;
  /** Names the best-fitting bar. */
  why: string;
}

/**
 * Upgrade a bare triad to its diatonic seventh (lofi flavour): i→i7, iv→iv7,
 * VI→VImaj7, III→IIImaj7, VII→VII7, V→V7, I→Imaj7, vii°→vii°7, … Chords that
 * already carry a suffix are returned unchanged.
 */
export function upgradeToSeventh(chord: ParsedChord, key: Key): ParsedChord {
  if (chord.quality !== 'maj' && chord.quality !== 'min' && chord.quality !== 'dim') return chord;
  const scale = scalePitchClasses(key);
  const idx = scale.indexOf(chord.rootPc);
  let seventh: number;
  if (idx >= 0) seventh = mod12(scale[(idx + 6) % 7]! - chord.rootPc);
  else if (key.mode === 'minor' && mod12(chord.rootPc - tonicPc(key)) === 11 && chord.quality === 'dim') seventh = 9;
  else return chord;
  let quality: ChordQuality | null = null;
  if (chord.quality === 'maj') quality = seventh === 11 ? 'maj7' : seventh === 10 ? 'dom7' : null;
  else if (chord.quality === 'min') quality = seventh === 10 ? 'min7' : seventh === 11 ? 'minmaj7' : null;
  else quality = seventh === 10 ? 'half_dim7' : seventh === 9 ? 'dim7' : null;
  if (!quality) return chord;
  return finalizeChord(chord.input, chord.rootPc, quality, key);
}

/**
 * Suggest progressions by scoring every template of the key's mode against the
 * melody (SPEC §4.3): per note, a chord tone scores 2 on steps 1/5/9/13 and 1
 * elsewhere, a diatonic non-chord tone 0, a chromatic note −1, each weighted by
 * length; the first three templates get a 0.5 prior. Scores are normalised by
 * the best achievable fit to 0..1. `why` names the best-fitting bar. With
 * `style: 'lofi'` the numerals are upgraded to sevenths after scoring.
 */
export function suggestChords(notes: Note[], key: Key, opts: { n?: number; style?: StyleId } = {}): ChordSuggestion[] {
  if (notes.length === 0) return [];
  const n = Math.max(1, Math.floor(opts.n ?? 3));
  const scale = scaleSet(key);
  const strong = (stepInBar: number): boolean => stepInBar % 4 === 0;
  const maxPossible =
    notes.reduce((s, note) => s + Math.max(1, note.length) * (strong(splitStep(note.step).stepInBar) ? 2 : 1), 0) + 0.5;
  const byBar: Note[][] = Array.from({ length: BARS }, () => []);
  for (const note of notes) byBar[splitStep(note.step).bar]!.push(note);

  const results = CHORD_TEMPLATES[key.mode].map((template, ti) => {
    const chords = template.map((numeral) => {
      const parsedChord = parseChordSymbol(numeral, key);
      if (isChordParseError(parsedChord)) throw new Error(`template numeral ${numeral} failed to parse: ${parsedChord.error}`);
      return parsedChord;
    });
    let total = ti < 3 ? 0.5 : 0;
    let bestBar = -1;
    let bestRatio = -1;
    let bestHits = 0;
    let bestCount = 0;
    chords.forEach((chord, bar) => {
      const pcs = new Set(chordPcs(chord.rootPc, chord.quality));
      let hits = 0;
      for (const note of byBar[bar]!) {
        const len = Math.max(1, note.length);
        const pc = mod12(note.pitch);
        const { stepInBar } = splitStep(note.step);
        if (pcs.has(pc)) {
          total += (strong(stepInBar) ? 2 : 1) * len;
          hits++;
        } else if (!scale.has(pc)) total -= len;
      }
      const count = byBar[bar]!.length;
      if (count > 0) {
        const ratio = hits / count;
        if (ratio > bestRatio) {
          bestRatio = ratio;
          bestBar = bar;
          bestHits = hits;
          bestCount = count;
        }
      }
    });
    const shown = opts.style === 'lofi' ? chords.map((c) => upgradeToSeventh(c, key)) : chords;
    const why =
      bestBar >= 0
        ? `bar ${bestBar + 1} fits best: ${bestHits} of ${bestCount} melody notes are chord tones of ${shown[bestBar]!.numeral} (${shown[bestBar]!.name})`
        : 'no melody notes to fit';
    return {
      raw: total,
      order: ti,
      suggestion: {
        progression: shown.map((c) => c.numeral),
        names: shown.map((c) => c.name),
        score: round3(Math.min(1, Math.max(0, total / maxPossible))),
        why,
      },
    };
  });
  results.sort((a, b) => b.raw - a.raw || a.order - b.order);
  return results.slice(0, n).map((r) => r.suggestion);
}

// ---------------------------------------------------------------------------
// Compact note format
// ---------------------------------------------------------------------------

/**
 * Compact description of notes for `get_track` (SPEC §4.3):
 * `b1: 1@1 3@5 5@9 | b2: …` — degree label (chromatic per `degreeOf`) `@`
 * step 1..16 within the bar, simultaneous notes low to high, empty bars as `-`.
 * `bars` are 1-based (as at the tool boundary); `lengths` appends `xN` for
 * notes longer than one step; `maxChars` truncates on a word boundary with `…`.
 */
export function describeNotes(
  notes: Note[],
  key: Key,
  opts: { bars?: number[]; maxChars?: number; lengths?: boolean } = {},
): string {
  const requested = opts.bars ?? [1, 2, 3, 4];
  const bars = [...new Set(requested.filter((b) => Number.isInteger(b) && b >= 1 && b <= BARS))].sort((a, b) => a - b);
  const parts = bars.map((bar) => {
    const lo = (bar - 1) * STEPS_PER_BAR;
    const inBar = notes
      .filter((n) => n.step >= lo && n.step < lo + STEPS_PER_BAR)
      .sort((a, b) => a.step - b.step || a.pitch - b.pitch);
    if (inBar.length === 0) return `b${bar}: -`;
    const tokens = inBar.map((n) => {
      const len = opts.lengths && n.length > 1 ? `x${n.length}` : '';
      return `${degreeOf(n.pitch, key).label}@${n.step - lo + 1}${len}`;
    });
    return `b${bar}: ${tokens.join(' ')}`;
  });
  const text = parts.join(' | ');
  const max = opts.maxChars;
  if (max === undefined || text.length <= max) return text;
  if (max <= 1) return '…'.slice(0, Math.max(0, max));
  let cut = text.slice(0, max - 1);
  const space = cut.lastIndexOf(' ');
  if (space > 0) cut = cut.slice(0, space);
  return `${cut.trimEnd()}…`;
}

// ---------------------------------------------------------------------------
// Note tokens (write_notes)
// ---------------------------------------------------------------------------

/**
 * Resolve a `write_notes` token to a pitch: `C4` / `Bb3` / `F#2` are absolute
 * (uppercase letter, so `b3` is unambiguously the lowered third);
 * `1`..`7` are scale degrees placed above the tonic in `octave` (default the
 * track's home octave from `TRACK_HOME_OCTAVE`, so `1` on lead in A minor is
 * A4 = 69); `b3` / `#4` lower or raise the degree by a semitone. `outOfKey`
 * is true when the resulting pitch class is not in the key's scale.
 */
export function resolveNoteToken(
  token: string,
  key: Key,
  track: MelodicTrackId,
  octave?: number,
): { pitch: number; outOfKey: boolean } | { error: string } {
  const text = normaliseSymbolText(token);
  const scale = scaleSet(key);
  const m = /^([b#]?)([1-7])$/.exec(text);
  if (!m && /^[A-G][#b]?-?\d{1,2}$/.test(text)) {
    const pitch = nameToPitch(text);
    if (pitch === null) return { error: `note '${token}' is outside the MIDI range; use C-1..G9` };
    return { pitch, outOfKey: !scale.has(mod12(pitch)) };
  }
  if (!m) {
    return { error: `unknown note token '${token}': use an absolute note like C4 or Bb3, a scale degree 1..7, or a chromatic degree like b3 or #4` };
  }
  const oct = octave ?? TRACK_HOME_OCTAVE[track];
  if (!Number.isInteger(oct) || oct < -1 || oct > 9) return { error: `octave ${octave} is out of range; use -1..9` };
  const degree = Number(m[2]);
  const acc = m[1] === 'b' ? -1 : m[1] === '#' ? 1 : 0;
  const pitch = (oct + 1) * 12 + tonicPc(key) + modeIntervals(key.mode)[degree - 1]! + acc;
  if (pitch < 0 || pitch > 127) return { error: `degree '${token}' in octave ${oct} is outside the MIDI range` };
  return { pitch, outOfKey: !scale.has(mod12(pitch)) };
}

// ---------------------------------------------------------------------------
// Transposition
// ---------------------------------------------------------------------------

/**
 * Shift every note by `semitones`, then wrap by octaves back into `range`.
 * Returns new note objects; the input is untouched.
 */
export function transposeNotes(notes: Note[], semitones: number, range: readonly [number, number]): Note[] {
  const [lo, hi] = range;
  return notes.map((n) => {
    let pitch = n.pitch + semitones;
    while (pitch < lo) pitch += 12;
    while (pitch > hi) pitch -= 12;
    return { ...n, pitch };
  });
}
